import { Col, Form, Row, Switch, Tooltip } from 'antd';
import { FC } from 'react';

import { CompanyDetailsWrapperFormControl } from '@entities';
import {
  type BillingSettings,
  DEFAULT_CCSS_ACD_INTERMEDIATION_PRICE,
  validateBillingSettingsFields,
} from '@utils';

import {
  INTERMEDIATION_ENABLED_FIELD,
  INTERMEDIATION_LABEL,
  INTERMEDIATION_PRICE_FIELD,
  INTERMEDIATION_PRICE_LABEL,
  INTERMEDIATION_TOOLTIP,
} from './billingIntermediation';

/**
 * Form.Item hands `checked`/`onChange` to its child, so the extra "pre-fill the default
 * fee" step has to ride along with the injected handler rather than replace it.
 */
const IntermediationSwitch: FC<{
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  onEnable: () => void;
}> = ({ checked, onChange, onEnable }) => (
  <Tooltip title={INTERMEDIATION_TOOLTIP}>
    <span>
      <Switch
        checked={!!checked}
        onChange={(nextChecked) => {
          onChange?.(nextChecked);

          if (nextChecked) {
            onEnable();
          }
        }}
      />
    </span>
  </Tooltip>
);

type BillingIntermediationFieldsProps = {
  /** Current billing settings read off the form, with prices coerced to numbers. */
  getBillingSettings: () => BillingSettings;
};

/**
 * "Intermediation with CCSS and ACD" toggle plus the monthly fee it bills.
 *
 * Kept out of the modal so it can be exercised in jsdom: the modal itself renders antd
 * `Select`s, whose injected CSS breaks every query in a vitest file (see
 * `CompanyDetailsServiceInfoEdit`), so its Select-free parts are tested from here.
 */
const BillingIntermediationFields: FC<BillingIntermediationFieldsProps> = ({
  getBillingSettings,
}) => {
  const form = Form.useFormInstance();
  const isEnabled = Form.useWatch(INTERMEDIATION_ENABLED_FIELD, form);

  // Turning the toggle on pre-fills the standard fee, so an operator only touches the
  // input for a company billed something other than the default. A price already worth
  // billing is left alone: re-enabling must not reset a negotiated amount. A stored 0 is
  // not worth billing -- the backend skips the line for it -- so it gets the default.
  const handleEnable = () => {
    const price: unknown = form.getFieldValue(INTERMEDIATION_PRICE_FIELD);

    if (!(typeof price === 'number' && price > 0)) {
      form.setFieldValue(
        INTERMEDIATION_PRICE_FIELD,
        DEFAULT_CCSS_ACD_INTERMEDIATION_PRICE
      );
    }
  };

  return (
    <>
      <Row gutter={[12, 0]}>
        <Col span={16}>
          <Form.Item
            label={INTERMEDIATION_LABEL}
            name={INTERMEDIATION_ENABLED_FIELD}
            valuePropName="checked"
          >
            <IntermediationSwitch onEnable={handleEnable} />
          </Form.Item>
        </Col>
      </Row>

      {isEnabled && (
        <Row gutter={[12, 0]}>
          <CompanyDetailsWrapperFormControl
            type="input-number"
            span={10}
            form={{
              label: INTERMEDIATION_PRICE_LABEL,
              name: INTERMEDIATION_PRICE_FIELD,
              dependencies: [INTERMEDIATION_ENABLED_FIELD],
              rules: [
                {
                  // Deliberately not gated on "billing enabled", unlike the sibling
                  // prices: an enabled toggle with no price is a state the backend
                  // answers by silently dropping the charge from every draft, so it must
                  // not be savable even while billing is off.
                  validator: () => {
                    const validation =
                      validateBillingSettingsFields(getBillingSettings());

                    if (validation.errors.ccssAcdIntermediationPrice) {
                      return Promise.reject(
                        new Error(validation.errors.ccssAcdIntermediationPrice)
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ],
            }}
            control={{
              style: { width: '100%' },
              min: 0,
            }}
          />
        </Row>
      )}
    </>
  );
};

export default BillingIntermediationFields;
